import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Loader2, Download, RefreshCw, ArrowUpRight, ArrowDownLeft,
  Building2, X, FileSpreadsheet, ChevronDown, ChevronUp, Upload,
  BarChart3, FileText, TrendingUp, TrendingDown, Pencil, Check, ExternalLink, Paperclip,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { ContactNotesTasksModal } from './ContactNotesTasksModal';
import CrmSalesReportModal from './CrmSalesReportModal';
import TeamReportViewerModal from './TeamReportViewerModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUS = ['Structural', 'Merchant', 'Industrial'];
const BU_OPTIONS = [{ value: '', label: 'None' }, ...BUS.map(b => ({ value: b, label: b }))];

const _fmtDateQuote = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return iso; }
};

// ── Persistence (localStorage) ──────────────────────────────────────────────
const LS_KEY = 'ms_last_state';
const PHOCAS_LS_KEY = 'ms_phocas_cache';

function loadCachedState() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || 'null'); } catch { return null; }
}
function saveCachedState(obj) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(obj)); } catch {}
}

function loadPhocasCache() {
  try {
    const raw = localStorage.getItem(PHOCAS_LS_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    // Rebuild normLookup / prevNormLookup from byName (not stored to save space)
    if (cached && cached.byName) {
      cached.normLookup = {};
      Object.keys(cached.byName).forEach(name => {
        cached.normLookup[_normName(name)] = cached.byName[name];
      });
    }
    if (cached && cached.prevByName) {
      cached.prevNormLookup = {};
      Object.keys(cached.prevByName).forEach(name => {
        cached.prevNormLookup[_normName(name)] = cached.prevByName[name];
      });
    }
    // Derive sectorTotals from byName + phocasSectors
    if (cached?.byName && cached?.phocasSectors) {
      cached.sectorTotals = _computeSectorTotals(cached.byName, cached.phocasSectors);
    }
    return cached;
  } catch { return null; }
}

function savePhocasCache(data) {
  if (!data) { try { localStorage.removeItem(PHOCAS_LS_KEY); } catch {} return; }
  try {
    // Don't store normLookup/prevNormLookup/sectorTotals — they're derived
    const toStore = {
      byName: data.byName,
      prevByName: data.prevByName || {},
      phocasSectors: data.phocasSectors || {},
      rowCount: data.rowCount,
      periodLabel: data.periodLabel,
      prevPeriodLabel: data.prevPeriodLabel,
      fileName: data.fileName || null,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(PHOCAS_LS_KEY, JSON.stringify(toStore));
  } catch { /* quota exceeded — silently ignore */ }
}
// ── Helpers ─────────────────────────────────────────────────────────────────
const _todayISO = () => new Date().toISOString().slice(0, 10);
const _monthStartISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const _fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(undefined, { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const _fmtDateShort = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return iso; }
};

const _fmtCcy = (v) => {
  if (v == null || v === '') return '';
  return `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// Convert ISO date to Phocas period label e.g. "2026-06-01" → "Jun 2026"
function _toPhocasPeriod(startDateISO) {
  if (!startDateISO) return null;
  try {
    const d = new Date(startDateISO + 'T00:00:00');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch { return null; }
}

function _prevPhocasPeriod(periodLabel) {
  if (!periodLabel) return null;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const parts = periodLabel.split(' ');
  if (parts.length !== 2) return null;
  const mIdx = months.indexOf(parts[0]);
  const year = parseInt(parts[1]);
  if (mIdx < 0 || isNaN(year)) return null;
  if (mIdx === 0) return `Dec ${year - 1}`;
  return `${months[mIdx - 1]} ${year}`;
}

// Fuzzy name normaliser for Phocas matching
const _normName = (s) => (s || '').toLowerCase()
  .replace(/\blimited\b|\bltd\.?\b|\bplc\b|\bllp\b|\binc\.?\b|\bco\.?\b/g, '')
  .replace(/[^a-z0-9 ]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

// Sector header detection — new Phocas export format has sector group headers
const SECTOR_HEADER_PATTERN = /^(STRUCTURAL|MERCHANT|MERCHAN|INDUSTRIAL|TECHNICAL|NEVILL\s*LONG)/i;
function _normalizeSectorName(raw) {
  const u = (raw || '').trim().toUpperCase();
  if (u.startsWith('STRUCTURAL')) return 'Structural';
  if (u.startsWith('MERCHANT') || u.startsWith('MERCHAN')) return 'Merchant';
  if (u.startsWith('INDUSTRIAL')) return 'Industrial';
  if (u.startsWith('TECHNICAL')) return 'Technical';
  if (u.startsWith('NEVILL')) return 'Nevill Long';
  const first = (raw || '').trim().split(/\s+/)[0] || '';
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}
function _computeSectorTotals(byName, phocasSectors) {
  const totals = {};
  Object.entries(byName || {}).forEach(([name, data]) => {
    const sector = (phocasSectors || {})[name];
    if (!sector) return;
    if (!totals[sector]) totals[sector] = { value: 0, contribution: 0, count: 0 };
    totals[sector].value += data.value || 0;
    totals[sector].contribution += data.contribution || 0;
    totals[sector].count++;
  });
  return totals;
}

// ── Phocas parser (handles multi-period pivot export + sector group headers) ──
// Month helpers for YoY classification
const _MONTHS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
function _normMonth(label) {
  return String(label || '').trim().toLowerCase()
    .replace('sept','sep').replace('sept.','sep');
}
function _parseMonthLabel(label) {
  const parts = _normMonth(label).split(/\s+/);
  if (parts.length !== 2) return null;
  const mIdx = _MONTHS.indexOf(parts[0]);
  const year = parseInt(parts[1]);
  if (mIdx < 0 || isNaN(year)) return null;
  return { mIdx, year };
}
function _isInPrevYear(label) {
  // Aug YYYY-1 → Jul YYYY  (where prev year = Aug 2024 – Jul 2025)
  const p = _parseMonthLabel(label);
  if (!p) return false;
  if (p.year === 2024 && p.mIdx >= 7) return true;
  if (p.year === 2025 && p.mIdx <= 6) return true;
  return false;
}
function _isInCurrYear(label) {
  // Aug YYYY → present  (where curr year = Aug 2025 – current)
  const p = _parseMonthLabel(label);
  if (!p) return false;
  if (p.year === 2025 && p.mIdx >= 7) return true;
  if (p.year >= 2026) return true;
  return false;
}

function parsePhocasSheet(file, selectedPeriodLabel) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];

        // Use raw 2D array to handle multi-row header pivot format
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        // Find the actual data header row (the row containing "Customer Name")
        let headerRowIdx = -1;
        let nameColIdx = -1;
        let codeColIdx = -1;
        let unitColIdx = -1;

        for (let i = 0; i < raw.length; i++) {
          const row = raw[i];
          const nIdx = row.findIndex(cell =>
            String(cell).trim().toLowerCase() === 'customer name'
          );
          if (nIdx >= 0) {
            headerRowIdx = i;
            nameColIdx = nIdx;
            // Accept "Code" or "Code Unit" column headers
            codeColIdx = row.findIndex(cell => String(cell).trim().toLowerCase().startsWith('code'));
            // Find "Unit" or "Unit Sector" column — when populated it marks sector header rows
            for (let k = 0; k < row.length; k++) {
              const lbl = String(row[k]).trim().toLowerCase();
              if (k !== codeColIdx && (lbl === 'unit' || lbl.startsWith('unit sector') || lbl === 'unit_sector')) {
                unitColIdx = k;
                break;
              }
            }
            break;
          }
        }

        if (headerRowIdx < 0) {
          reject(new Error('Could not find "Customer Name" column. Check the Phocas export format.'));
          return;
        }

        const headerRow = raw[headerRowIdx];

        // ── Detect Group Description column (new format indicator) ──────────
        const grpColIdx = headerRow.findIndex(h =>
          String(h).trim().toLowerCase() === 'group description'
        );
        const hasNewFormat = grpColIdx >= 0;

        // Find Value / Contribution / Contribution % columns for the selected period
        let valueColIdx = -1, contribColIdx = -1, contribPctColIdx = -1;

        if (selectedPeriodLabel) {
          // Scan rows above the header for period labels
          for (let i = headerRowIdx - 1; i >= 0; i--) {
            const periodRow = raw[i];
            const periodCols = [];
            periodRow.forEach((cell, idx) => {
              if (String(cell).trim() === selectedPeriodLabel) periodCols.push(idx);
            });
            if (periodCols.length > 0) {
              periodCols.forEach(colIdx => {
                const subLabel = String(headerRow[colIdx]).trim().toLowerCase();
                if (subLabel === 'value' && valueColIdx < 0) valueColIdx = colIdx;
                else if (subLabel === 'contribution' && !subLabel.includes('%') && contribColIdx < 0) contribColIdx = colIdx;
                else if (subLabel.includes('%') && contribPctColIdx < 0) contribPctColIdx = colIdx;
              });
              if (valueColIdx >= 0) break;
            }
          }
        }

        // Fall back to first Value/Contribution columns after Customer Name
        if (valueColIdx < 0) {
          for (let i = nameColIdx + 1; i < headerRow.length; i++) {
            const lbl = String(headerRow[i]).trim().toLowerCase();
            if (lbl === 'value' && valueColIdx < 0) valueColIdx = i;
            else if (lbl === 'contribution' && !lbl.includes('%') && contribColIdx < 0) contribColIdx = i;
            else if (lbl.includes('%') && contribPctColIdx < 0) contribPctColIdx = i;
            if (valueColIdx >= 0 && contribColIdx >= 0 && contribPctColIdx >= 0) break;
          }
        }

        // ── Flat-header format: "Jun 2026 Value", "Jun 2026 Contribution %" ────
        if (valueColIdx < 0 && selectedPeriodLabel) {
          const pl = selectedPeriodLabel.toLowerCase();
          for (let i = nameColIdx + 1; i < headerRow.length; i++) {
            const lbl = String(headerRow[i]).trim().toLowerCase();
            if (!lbl.startsWith(pl)) continue;
            const suffix = lbl.slice(pl.length).trim();
            if (suffix === 'value' && valueColIdx < 0) valueColIdx = i;
            else if (suffix === 'contribution' && contribColIdx < 0) contribColIdx = i;
            else if ((suffix === 'contribution %' || suffix === 'contribution%') && contribPctColIdx < 0) contribPctColIdx = i;
          }
        }

        // Parse data rows
        const byName = {};
        const prevByName = {};
        const phocasSectors = {};
        let currentSector = null;
        const dataRows = raw.slice(headerRowIdx + 1);

        // Also find previous period columns
        const prevPeriodLabel = _prevPhocasPeriod(selectedPeriodLabel);
        let prevValueColIdx = -1, prevContribColIdx = -1, prevContribPctColIdx = -1;
        if (prevPeriodLabel) {
          for (let i = headerRowIdx - 1; i >= 0; i--) {
            const periodRow = raw[i];
            const periodCols = [];
            periodRow.forEach((cell, idx) => {
              if (String(cell).trim() === prevPeriodLabel) periodCols.push(idx);
            });
            if (periodCols.length > 0) {
              periodCols.forEach(colIdx => {
                const subLabel = String(headerRow[colIdx]).trim().toLowerCase();
                if (subLabel === 'value' && prevValueColIdx < 0) prevValueColIdx = colIdx;
                else if (subLabel === 'contribution' && !subLabel.includes('%') && prevContribColIdx < 0) prevContribColIdx = colIdx;
                else if (subLabel.includes('%') && prevContribPctColIdx < 0) prevContribPctColIdx = colIdx;
              });
              if (prevValueColIdx >= 0) break;
            }
          }
          // Flat-header fallback for previous period
          if (prevValueColIdx < 0) {
            const pl = prevPeriodLabel.toLowerCase();
            for (let i = nameColIdx + 1; i < headerRow.length; i++) {
              const lbl = String(headerRow[i]).trim().toLowerCase();
              if (!lbl.startsWith(pl)) continue;
              const suffix = lbl.slice(pl.length).trim();
              if (suffix === 'value' && prevValueColIdx < 0) prevValueColIdx = i;
              else if (suffix === 'contribution' && prevContribColIdx < 0) prevContribColIdx = i;
              else if ((suffix === 'contribution %' || suffix === 'contribution%') && prevContribPctColIdx < 0) prevContribPctColIdx = i;
            }
          }
        }

        // ── Flat-format sector column detection ────────────────────────────────
        let defaultSector = null;
        for (let i = 0; i < headerRow.length; i++) {
          const m = String(headerRow[i]).trim().match(SECTOR_HEADER_PATTERN);
          if (m) { defaultSector = _normalizeSectorName(m[0]); break; }
        }

        // ── Build all-months column map (new format only) ───────────────────
        // Maps month label → { valIdx, contribIdx, pctIdx }
        const allMonthCols = {};
        if (hasNewFormat) {
          let ci = nameColIdx + 1;
          while (ci < headerRow.length) {
            const h = String(headerRow[ci] || '').trim();
            const hl = h.toLowerCase();
            if (hl.endsWith(' value')) {
              const monthLabel = h.slice(0, -6).trim(); // strip " Value"
              if (_normMonth(monthLabel) !== 'total') {
                allMonthCols[monthLabel] = { valIdx: ci, contribIdx: ci + 1, pctIdx: ci + 2 };
                ci += 3;
                continue;
              }
            }
            ci++;
          }
        }
        const currYearMonthCount = Object.keys(allMonthCols).filter(m => _isInCurrYear(m)).length || 11;

        // ── First pass: standard byName + prevByName per customer ──────────
        dataRows.forEach(row => {
          const nameVal = String(row[nameColIdx] || '').trim();
          const codeVal = codeColIdx >= 0 ? String(row[codeColIdx] || '').trim() : '';
          const grpVal = grpColIdx >= 0 ? String(row[grpColIdx] || '').trim() : '';

          // Skip product group rows (they have no Customer Name)
          if (grpVal && !nameVal) return;

          // Unit-column sector detection
          if (unitColIdx >= 0) {
            const unitVal = String(row[unitColIdx] || '').trim();
            if (unitVal && codeVal) {
              currentSector = _normalizeSectorName(codeVal);
              return;
            }
          }

          // Fallback sector detection
          let sectorCandidate = null;
          const checkCells = [codeVal, nameVal];
          for (let ci = 0; ci <= Math.min(nameColIdx, 4); ci++) {
            checkCells.push(String(row[ci] || '').trim());
          }
          for (const cell of checkCells) {
            const m = cell.match(SECTOR_HEADER_PATTERN);
            if (m) { sectorCandidate = m[0]; break; }
          }
          if (sectorCandidate) {
            const valCell = valueColIdx >= 0 ? row[valueColIdx] : null;
            const hasRealValue = valCell !== null && valCell !== '' && valCell !== 0 &&
              !isNaN(Number(valCell)) && Math.abs(Number(valCell)) > 0;
            if (!hasRealValue) {
              currentSector = _normalizeSectorName(sectorCandidate);
              return;
            }
          }

          const name = nameVal;
          if (!name || name.toUpperCase() === 'TOTAL' || name.toUpperCase().startsWith('TOTAL') ||
              name.toUpperCase() === 'AVERAGE') return;

          const value = parseFloat(String(row[valueColIdx] ?? '0').replace(/[£,\s]/g, '')) || 0;
          const contrib = parseFloat(String(row[contribColIdx] ?? '0').replace(/[£,\s]/g, '')) || 0;
          const contribPct = parseFloat(String(row[contribPctColIdx] ?? '0').replace(/[%,\s]/g, '')) || 0;
          byName[name] = { name, value, contribution: contrib, contrib_pct: contribPct };
          if (currentSector) phocasSectors[name] = currentSector;
          else if (defaultSector) phocasSectors[name] = defaultSector;

          if (prevValueColIdx >= 0) {
            const pv = parseFloat(String(row[prevValueColIdx] ?? '0').replace(/[£,\s]/g, '')) || 0;
            const pc = parseFloat(String(row[prevContribColIdx] ?? '0').replace(/[£,\s]/g, '')) || 0;
            const pp = parseFloat(String(row[prevContribPctColIdx] ?? '0').replace(/[%,\s]/g, '')) || 0;
            prevByName[name] = { name, value: pv, contribution: pc, contrib_pct: pp };
          }
        });

        // ── Second pass: extract product groups + year aggregates (new format) ─
        const productGroupsByName = {};
        const prevYearByName = {};
        const currYearByName = {};

        if (hasNewFormat && Object.keys(allMonthCols).length > 0) {
          let curCust = null;
          let curSector2 = null;

          dataRows.forEach(row => {
            const codeVal = codeColIdx >= 0 ? String(row[codeColIdx] || '').trim() : '';
            const unitVal = unitColIdx >= 0 ? String(row[unitColIdx] || '').trim() : '';
            const custVal = String(row[nameColIdx] || '').trim();
            const grpVal = String(row[grpColIdx] || '').trim();

            // Sector header row
            if (unitVal && codeVal) {
              curSector2 = _normalizeSectorName(codeVal);
              curCust = null;
              return;
            }
            // Customer row (Customer Name populated, Group Description empty)
            if (custVal && !grpVal) {
              const upper = custVal.toUpperCase();
              if (upper === 'TOTAL' || upper === 'AVERAGE' || upper.startsWith('TOTAL')) {
                curCust = null;
                return;
              }
              curCust = custVal;
              if (!productGroupsByName[curCust]) productGroupsByName[curCust] = [];
              return;
            }
            // Product group row (Group Description populated, Customer Name empty)
            if (grpVal && !custVal && curCust) {
              const g = {
                code: codeVal,
                name: grpVal,
                prevYear: { value: 0, contribution: 0, contrib_pct: 0 },
                currYear: { value: 0, contribution: 0, contrib_pct: 0 },
              };
              Object.entries(allMonthCols).forEach(([mLabel, cols]) => {
                const v = parseFloat(row[cols.valIdx] ?? 0) || 0;
                const c = parseFloat(row[cols.contribIdx] ?? 0) || 0;
                if (_isInPrevYear(mLabel)) {
                  g.prevYear.value += v;
                  g.prevYear.contribution += c;
                } else if (_isInCurrYear(mLabel)) {
                  g.currYear.value += v;
                  g.currYear.contribution += c;
                }
              });
              g.prevYear.contrib_pct = g.prevYear.value ? (g.prevYear.contribution / g.prevYear.value) * 100 : 0;
              g.currYear.contrib_pct = g.currYear.value ? (g.currYear.contribution / g.currYear.value) * 100 : 0;
              if (Math.abs(g.prevYear.value) > 0 || Math.abs(g.currYear.value) > 0) {
                productGroupsByName[curCust].push(g);
              }
            }
          });

          // Compute year-level customer totals from their product groups
          Object.entries(productGroupsByName).forEach(([custName, groups]) => {
            let py = { value: 0, contribution: 0 };
            let cy = { value: 0, contribution: 0 };
            groups.forEach(g => {
              py.value += g.prevYear.value;
              py.contribution += g.prevYear.contribution;
              cy.value += g.currYear.value;
              cy.contribution += g.currYear.contribution;
            });
            prevYearByName[custName] = {
              value: py.value,
              contribution: py.contribution,
              contrib_pct: py.value ? (py.contribution / py.value) * 100 : 0,
            };
            currYearByName[custName] = {
              value: cy.value,
              contribution: cy.contribution,
              contrib_pct: cy.value ? (cy.contribution / cy.value) * 100 : 0,
              // Annualised run rate (extrapolate months-to-date to 12)
              run_rate_value: cy.value ? (cy.value / currYearMonthCount) * 12 : 0,
              run_rate_contribution: cy.contribution ? (cy.contribution / currYearMonthCount) * 12 : 0,
              run_rate_contrib_pct: cy.value ? (cy.contribution / cy.value) * 100 : 0,
            };
          });
        }

        const normLookup = {};
        Object.keys(byName).forEach(name => { normLookup[_normName(name)] = byName[name]; });
        const prevNormLookup = {};
        Object.keys(prevByName).forEach(name => { prevNormLookup[_normName(name)] = prevByName[name]; });
        const sectorTotals = _computeSectorTotals(byName, phocasSectors);

        resolve({
          byName, normLookup, rowCount: Object.keys(byName).length, periodLabel: selectedPeriodLabel,
          prevByName, prevNormLookup, prevPeriodLabel,
          phocasSectors, sectorTotals,
          // New: year-level aggregates + product groups
          prevYearByName, currYearByName, productGroupsByName,
          hasNewFormat,
        });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsBinaryString(file);
  });
}

// Match an account name to Phocas data (exact then fuzzy)
function matchPhocas(accountName, normLookup) {
  if (!accountName || !normLookup) return null;
  const norm = _normName(accountName);
  if (normLookup[norm]) return normLookup[norm];
  for (const [key, val] of Object.entries(normLookup)) {
    if (norm.length >= 4 && (key.includes(norm) || norm.includes(key))) return val;
  }
  return null;
}

// ── Export dialog ─────────────────────────────────────────────────────────────
const ALL_COLS = [
  { key: 'owner_name',    label: 'Rep Name' },
  { key: 'account_name',  label: 'Account Name' },
  { key: 'contact_name',  label: 'Contact Name' },
  { key: 'call_start_time', label: 'Date / Time' },
  { key: 'call_purpose',  label: 'Call Purpose' },
  { key: 'direction',     label: 'Direction' },
  { key: 'business_unit', label: 'Business Unit' },
  { key: 'subject',       label: 'Subject' },
  { key: 'notes',         label: 'Notes' },
];

function ExportModal({ calls, onClose }) {
  const [selectedCols, setSelectedCols] = useState(new Set(ALL_COLS.map(c => c.key)));
  const [groupAll, setGroupAll] = useState(false);

  const toggleCol = (key) => {
    setSelectedCols(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const doExport = () => {
    const cols = ALL_COLS.filter(c => selectedCols.has(c.key));
    const wb = XLSX.utils.book_new();
    const toRow = (c) => {
      const row = {};
      cols.forEach(({ key, label }) => {
        let val = c[key] ?? '';
        if (key === 'call_start_time') val = _fmtDate(val);
        if (key === 'direction') val = val ? val.charAt(0).toUpperCase() + val.slice(1) : '';
        row[label] = val;
      });
      return row;
    };
    if (groupAll) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(calls.map(toRow)), 'All Calls');
    } else {
      const buGroups = {};
      calls.forEach(c => { const bu = c.business_unit || 'Unknown'; if (!buGroups[bu]) buGroups[bu] = []; buGroups[bu].push(c); });
      const keys = [...BUS.filter(b => buGroups[b]), ...(buGroups['Unknown'] ? ['Unknown'] : [])];
      (keys.length ? keys : Object.keys(buGroups)).forEach(bu => {
        const rows = (buGroups[bu] || []).map(toRow);
        if (rows.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), bu.slice(0, 31));
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(calls.map(toRow)), 'All Calls');
    }
    XLSX.writeFile(wb, `market-sector-calls-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Spreadsheet downloaded');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="export-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" data-testid="export-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-sky-600" /> Export to Spreadsheet
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="export-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="mb-4">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Columns to include</p>
          <div className="grid grid-cols-2 gap-1.5">
            {ALL_COLS.map(c => (
              <label key={c.key} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                <input type="checkbox" checked={selectedCols.has(c.key)} onChange={() => toggleCol(c.key)} className="rounded accent-sky-600" />
                {c.label}
              </label>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 mb-5">
          <input type="checkbox" checked={groupAll} onChange={e => setGroupAll(e.target.checked)} className="rounded accent-sky-600" />
          Group all business units into a single tab
        </label>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl">Cancel</Button>
          <Button onClick={doExport} disabled={!selectedCols.size} className="flex-1 rounded-xl text-white"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}>
            <Download className="w-4 h-4 mr-1.5" /> Download
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Chip ─────────────────────────────────────────────────────────────────────
function Chip({ label, active, onClick, testId, color = 'sky' }) {
  const activeStyle = color === 'orange'
    ? { background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)', color: '#fff' }
    : { background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', color: '#fff' };
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${active ? 'border-transparent shadow-sm' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
      style={active ? activeStyle : undefined}>
      {label}
    </button>
  );
}

// ── Quotes detail modal ───────────────────────────────────────────────────────
function QuotesDetailModal({ accountName, quotes, onClose }) {
  const [pdfLoading, setPdfLoading] = useState(null);

  const openPdf = async (quoteId) => {
    if (pdfLoading) return;
    setPdfLoading(quoteId);
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`${API}/zoho/quotes/${quoteId}/pdf-download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const win = window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
      if (!win) toast.error('Popup blocked — please allow popups');
    } catch {
      toast.error('No PDF attached to this quote');
    } finally {
      setPdfLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="quotes-detail-modal-overlay" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="quotes-detail-modal">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Quotes — {accountName}</h3>
            <p className="text-[10px] text-gray-400 mt-0.5">{quotes.length} quote{quotes.length !== 1 ? 's' : ''} in date range</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded" data-testid="quotes-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-3 space-y-1.5">
          {quotes.length === 0 && <p className="text-xs text-gray-400 italic text-center py-6">No quotes found for this period.</p>}
          {quotes.map(q => (
            <div key={q.id} className="flex items-center gap-2 rounded-lg border border-gray-100 px-3 py-2 hover:bg-gray-50 transition-colors">
              <FileText className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-900 truncate">{q.subject || 'Untitled'}</p>
                {q.created_at && (
                  <p className="text-[10px] text-gray-400">{_fmtDateQuote(q.created_at)}</p>
                )}
                {q.stage && (
                  <span className="text-[9px] bg-purple-50 text-purple-600 rounded px-1.5 py-0.5 font-medium">{q.stage}</span>
                )}
              </div>
              <span className="text-xs font-bold text-gray-900 flex-shrink-0 tabular-nums">
                {q.total > 0 ? `£${Number(q.total).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
              </span>
              <button
                type="button"
                onClick={() => openPdf(q.id)}
                disabled={pdfLoading === q.id}
                className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50 flex-shrink-0"
                title="Open PDF"
                data-testid={`qdm-pdf-${q.id}`}
              >
                {pdfLoading === q.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
                PDF
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── 4-column Call row ─────────────────────────────────────────────────────────
function CallRow({ call, quotesInfo, phocasNormLookup, phocasPrevNormLookup, phocasPeriodLabel, phocasPrevPeriodLabel, onOpenCustomer, onNoteUpdated, onBuUpdated, onOpenQuotes, selected, onToggleSelect }) {
  const [isEditing, setIsEditing] = useState(false);
  const [noteValue, setNoteValue] = useState(call.notes || '');
  const [saving, setSaving] = useState(false);
  const [buSaving, setBuSaving] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!isEditing) setNoteValue(call.notes || '');
  }, [call.notes, isEditing]);

  const startEdit = () => {
    setNoteValue(call.notes || '');
    setIsEditing(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const cancelEdit = () => {
    setNoteValue(call.notes || '');
    setIsEditing(false);
  };

  const saveNote = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await api.patch(`${API}/zoho/scheduled-call/${call.id}`, {
        new_description: noteValue.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      onNoteUpdated(call.id, noteValue.trim());
      setIsEditing(false);
      toast.success('Notes saved to CRM');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const handleBuChange = async (newBu) => {
    if (buSaving) return;
    const prev = call.business_unit;
    onBuUpdated(call.id, newBu); // optimistic
    setBuSaving(true);
    try {
      const token = localStorage.getItem('token');
      await api.post(`${API}/zoho/call-metadata`, {
        zoho_call_id: call.id,
        business_unit: newBu || null,
        direction: call.direction,
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch {
      onBuUpdated(call.id, prev); // revert
      toast.error('Failed to update business unit');
    } finally {
      setBuSaving(false);
    }
  };

  const dirIcon = call.direction === 'incoming'
    ? <ArrowDownLeft className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
    : <ArrowUpRight className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />;

  const phocasMatch = phocasNormLookup ? matchPhocas(call.account_name, phocasNormLookup) : null;
  const phocasPrev  = phocasPrevNormLookup ? matchPhocas(call.account_name, phocasPrevNormLookup) : null;

  const valueDelta = (phocasMatch && phocasPrev) ? phocasMatch.value - phocasPrev.value : null;
  const deltaPositive = valueDelta !== null && valueDelta >= 0;

  return (
    <div className="border-b border-gray-100 last:border-0 py-3 px-3 hover:bg-gray-50/60 transition-colors" data-testid={`call-row-${call.id}`}>
      <div className="grid gap-3" style={{ gridTemplateColumns: '1.8fr 0.75fr 1.3fr 1.5fr' }}>

        {/* Col 1: Account, contact, rep, enquiry type */}
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            {onToggleSelect && (
              <input
                type="checkbox"
                checked={!!selected}
                onChange={() => onToggleSelect(call)}
                onClick={e => e.stopPropagation()}
                className="w-3.5 h-3.5 rounded border-gray-300 text-orange-500 focus:ring-orange-400 cursor-pointer flex-shrink-0"
                data-testid={`call-select-checkbox-${call.id}`}
                title="Select for bulk call scheduling"
              />
            )}
            {dirIcon}
            <button
              type="button"
              onClick={() => onOpenCustomer(call.account_name)}
              className="font-semibold text-sm text-blue-700 hover:text-blue-900 hover:underline truncate text-left transition-colors"
              title="Open customer details"
              data-testid={`call-account-link-${call.id}`}
            >
              {call.account_name || '—'}
            </button>
          </div>
          {call.contact_name && (
            <p className="text-xs text-gray-900 truncate pl-5 mb-0.5">{call.contact_name}</p>
          )}
          <div className="flex flex-wrap items-center gap-1 pl-5">
            <span className="text-xs font-medium text-gray-900">{call.owner_name || '—'}</span>
            {call.call_purpose && (
              <span className="text-xs bg-gray-100 rounded px-1.5 py-0.5 text-gray-700">{call.call_purpose}</span>
            )}
          </div>
        </div>

        {/* Col 2: Date + BU dropdown only */}
        <div className="min-w-0 space-y-1.5">
          <p className="text-xs text-gray-700 font-medium">{_fmtDateShort(call.call_start_time)}</p>

          {/* Business Unit — always-visible dropdown */}
          <div className="relative">
            <select
              value={call.business_unit || ''}
              onChange={e => handleBuChange(e.target.value)}
              disabled={buSaving}
              className={`appearance-none text-xs font-semibold rounded px-2 py-1 pr-5 cursor-pointer border transition-colors focus:outline-none focus:ring-1 focus:ring-sky-400 ${
                call.business_unit
                  ? 'bg-sky-100 text-sky-700 border-sky-200 hover:bg-sky-200'
                  : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'
              }`}
              data-testid={`bu-select-${call.id}`}
              title="Change business unit"
            >
              {BU_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-1 top-1 w-3 h-3 pointer-events-none text-gray-400" />
          </div>
        </div>

        {/* Col 3: Phocas data + Quotes */}
        <div className="min-w-0 space-y-1.5">
          {/* Quotes — clickable pill */}
          {quotesInfo != null && quotesInfo.count > 0 && (
            <button
              type="button"
              onClick={() => onOpenQuotes(call)}
              className="flex items-center gap-1.5 flex-wrap hover:opacity-80 transition-opacity"
              data-testid={`quote-badge-${call.id}`}
              title="View quote details"
            >
              <BarChart3 className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
              <span className="text-xs font-bold text-purple-700 underline decoration-dotted">
                {quotesInfo.count} quote{quotesInfo.count !== 1 ? 's' : ''}
                {quotesInfo.total_value > 0 && <span className="font-semibold text-purple-600"> · {_fmtCcy(quotesInfo.total_value)}</span>}
              </span>
            </button>
          )}

          {phocasMatch ? (
            <div className="space-y-1" data-testid={`phocas-badge-${call.id}`}>
              {/* Row 1: Growth delta — only if prev period is available */}
              {phocasPrev && valueDelta !== null && (
                <div className="flex items-center gap-1">
                  {deltaPositive
                    ? <TrendingUp className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                    : <TrendingDown className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                  }
                  <span className={`text-xs font-bold ${deltaPositive ? 'text-emerald-600' : 'text-red-500'}`}>
                    {deltaPositive ? '+' : ''}{_fmtCcy(valueDelta)}
                  </span>
                  {phocasPrevPeriodLabel && <span className="text-[10px] text-gray-400">vs {phocasPrevPeriodLabel}</span>}
                </div>
              )}
              {/* Row 2: Current period value */}
              <div className="flex items-center gap-1">
                <span className="text-xs font-bold text-gray-900">{_fmtCcy(phocasMatch.value)}</span>
                {phocasPeriodLabel && <span className="text-[10px] text-gray-500">{phocasPeriodLabel}</span>}
              </div>
              {/* Row 3: Contribution £ + % */}
              {(phocasMatch.contribution > 0 || phocasMatch.contrib_pct > 0) && (
                <div className="flex items-center gap-1.5">
                  {phocasMatch.contribution > 0 && (
                    <span className="text-[11px] text-gray-700 font-medium">{_fmtCcy(phocasMatch.contribution)}</span>
                  )}
                  {phocasMatch.contrib_pct != null && phocasMatch.contrib_pct !== 0 && (
                    <span className="text-[11px] text-gray-500">{phocasMatch.contrib_pct.toFixed(1)}%</span>
                  )}
                </div>
              )}
            </div>
          ) : (
            <span className="text-xs text-gray-400 italic">No Phocas data</span>
          )}
        </div>

        {/* Col 4: Notes — always editable */}
        <div className="min-w-0">
          {isEditing ? (
            <div className="space-y-1.5" data-testid={`note-edit-${call.id}`}>
              <textarea
                ref={textareaRef}
                value={noteValue}
                onChange={e => setNoteValue(e.target.value)}
                rows={3}
                placeholder="Type call notes here…"
                className="w-full rounded-lg border border-sky-300 bg-white text-xs text-gray-900 px-2 py-1.5 resize-y focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
                data-testid={`note-textarea-${call.id}`}
              />
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={saveNote}
                  disabled={saving}
                  className="flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-md bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50 transition-colors"
                  data-testid={`note-save-${call.id}`}
                >
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  {saving ? 'Saving…' : 'Save to CRM'}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  disabled={saving}
                  className="text-xs text-gray-500 hover:text-gray-700 px-1.5 py-1 rounded transition-colors"
                  data-testid={`note-cancel-${call.id}`}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="group relative" data-testid={`note-display-${call.id}`}>
              {call.notes ? (
                <div className="flex items-start gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-900 whitespace-pre-wrap leading-relaxed flex-1">{call.notes}</p>
                </div>
              ) : (
                <span className="text-xs text-gray-400 italic">No notes — click to add</span>
              )}
              <button
                type="button"
                onClick={startEdit}
                className="absolute -top-0.5 right-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                title="Edit notes"
                data-testid={`note-edit-btn-${call.id}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Owner group ────────────────────────────────────────────────────────────────
function OwnerGroup({ ownerName, calls, quotesData, phocasNormLookup, phocasPrevNormLookup, phocasPeriodLabel, phocasPrevPeriodLabel, onOpenCustomer, onNoteUpdated, onBuUpdated, onOpenQuotes, selectedIds, onToggleSelect }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden mb-3">
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-gray-50 to-white hover:bg-gray-50 transition-colors"
        data-testid={`owner-group-${ownerName}`}
      >
        <div className="flex items-center gap-2">
          <span className="font-bold text-sm text-gray-900">{ownerName || 'Unknown Rep'}</span>
          <span className="text-xs bg-sky-100 text-sky-700 rounded-full px-2 py-0.5 font-semibold">{calls.length}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div>
          {/* Column header row */}
          <div className="grid gap-3 px-3 py-1.5 bg-gray-50 border-b border-gray-100 text-[10px] font-bold uppercase tracking-wider text-gray-400"
            style={{ gridTemplateColumns: '1.8fr 0.75fr 1.3fr 1.5fr' }}>
            <span>Account / Contact / Rep</span>
            <span>Date · BU</span>
            <span>Quotes · Phocas{phocasPeriodLabel ? ` (${phocasPeriodLabel})` : ''}</span>
            <span>Notes</span>
          </div>
          {calls.map(c => (
            <CallRow
              key={c.id}
              call={c}
              quotesInfo={quotesData ? (quotesData[c.account_id] ?? null) : null}
              phocasNormLookup={phocasNormLookup}
              phocasPrevNormLookup={phocasPrevNormLookup}
              phocasPeriodLabel={phocasPeriodLabel}
              phocasPrevPeriodLabel={phocasPrevPeriodLabel}
              onOpenCustomer={onOpenCustomer}
              onNoteUpdated={onNoteUpdated}
              onBuUpdated={onBuUpdated}
              onOpenQuotes={onOpenQuotes}
              selected={onToggleSelect ? selectedIds?.has(c.account_id) : undefined}
              onToggleSelect={onToggleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Phocas drag-drop zone ─────────────────────────────────────────────────────
function PhocasUploadZone({ onParsed, currentData, selectedPeriodLabel }) {
  const fileRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [parsing, setParsing] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setParsing(true);
    try {
      const result = await parsePhocasSheet(file, selectedPeriodLabel);
      onParsed(result, file.name);
      const periodInfo = result.periodLabel ? ` (${result.periodLabel}` : '';
      const prevInfo = result.prevPeriodLabel ? ` vs ${result.prevPeriodLabel})` : (periodInfo ? ')' : '');
      toast.success(`Phocas data loaded — ${result.rowCount} accounts${periodInfo}${prevInfo}`);
    } catch (err) {
      toast.error(`Failed to parse Phocas file: ${err.message}`);
    } finally {
      setParsing(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  // Human-readable saved-at date
  const savedAtLabel = currentData?.savedAt
    ? (() => {
        try {
          return new Date(currentData.savedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
        } catch { return null; }
      })()
    : null;

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      className={`rounded-xl border-2 border-dashed px-4 py-3 flex items-center gap-3 cursor-pointer transition-all ${
        dragging ? 'border-sky-400 bg-sky-50' : currentData ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:border-sky-300 hover:bg-sky-50/30'
      }`}
      onClick={() => fileRef.current?.click()}
      data-testid="phocas-upload-zone"
    >
      <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
      {parsing ? (
        <Loader2 className="w-4 h-4 animate-spin text-sky-500 flex-shrink-0" />
      ) : (
        <Upload className={`w-4 h-4 flex-shrink-0 ${currentData ? 'text-green-600' : 'text-gray-400'}`} />
      )}
      <div className="min-w-0 flex-1">
        {currentData ? (
          <>
            <p className="text-xs font-bold text-green-700">
              Phocas data loaded — {currentData.rowCount} accounts
              {currentData.periodLabel ? ` · ${currentData.periodLabel}` : ''}
            </p>
            <p className="text-[10px] text-gray-500">
              {savedAtLabel ? `Uploaded ${savedAtLabel}` : 'Saved locally'}
              {currentData.uploaded_by ? ` by ${currentData.uploaded_by.split('@')[0]}` : ''}
              {currentData.fileName ? ` · ${currentData.fileName}` : ''}
              {' · Drop new file or click to replace'}
            </p>
          </>
        ) : (
          <>
            <p className="text-xs font-semibold text-gray-700">Upload Phocas Data</p>
            <p className="text-[10px] text-gray-400">
              Drag & drop or click — Excel (.xlsx) only
              {selectedPeriodLabel ? ` · Will match ${selectedPeriodLabel} data` : ''}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ── Named exports for reuse in other pages (e.g. SalesHubPage) ───────────────
export {
  parsePhocasSheet, _toPhocasPeriod, _normName, _normalizeSectorName, _computeSectorTotals,
  ExportModal, Chip, CallRow, OwnerGroup, QuotesDetailModal, PhocasUploadZone,
  _fmtCcy, BUS, BU_OPTIONS, matchPhocas, _prevPhocasPeriod,
};

// ── Main component ─────────────────────────────────────────────────────────────
export default function MarketSectorView() {
  const cached = loadCachedState();

  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(cached?.selectedTeam || '');
  const [startDate, setStartDate] = useState(cached?.startDate || _monthStartISO());
  const [endDate, setEndDate] = useState(cached?.endDate || _todayISO());
  const [calls, setCalls] = useState(cached?.calls || []);
  const [quotesData, setQuotesData] = useState(cached?.quotesData || null);
  const [phocasData, setPhocasData] = useState(() => loadPhocasCache());
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(!!cached?.calls?.length);
  const [showExport, setShowExport] = useState(false);
  const [customerModalName, setCustomerModalName] = useState(null);

  // Filters
  const [buFilters, setBuFilters] = useState(new Set());
  const [dirFilters, setDirFilters] = useState(new Set());

  // Phocas data mode: false = show ALL Phocas data (default), true = calls only
  const [callsOnly, setCallsOnly] = useState(false);

  // Derive the Phocas period label from the selected date range
  const phocasPeriodLabel = _toPhocasPeriod(startDate);

  // ── Load global Phocas data from DB on mount ───────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('token');
    api.get(`${API}/zoho/phocas-data`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const dbData = r.data?.data;
        if (dbData && dbData.byName) {
          // Rebuild normLookup in-browser (not stored in DB to save space)
          dbData.normLookup = {};
          Object.keys(dbData.byName).forEach(name => {
            dbData.normLookup[_normName(name)] = dbData.byName[name];
          });
          if (dbData.prevByName) {
            dbData.prevNormLookup = {};
            Object.keys(dbData.prevByName).forEach(name => {
              dbData.prevNormLookup[_normName(name)] = dbData.prevByName[name];
            });
          }
          // Derive sectorTotals from byName + phocasSectors
          if (dbData.phocasSectors) {
            dbData.sectorTotals = _computeSectorTotals(dbData.byName, dbData.phocasSectors);
          }
          setPhocasData(dbData);
          savePhocasCache(dbData); // keep local cache in sync
        }
      })
      .catch(() => {}); // silently ignore — local cache already loaded from useState init
  }, []); // eslint-disable-line

  useEffect(() => {
    const token = localStorage.getItem('token');
    api.get(`${API}/teams`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const list = r.data?.teams || [];
        setTeams(list);
        if (list.length && !selectedTeam) setSelectedTeam(list[0].id);
      })
      .catch(() => toast.error('Failed to load teams'));
  }, []); // eslint-disable-line

  const toggleBu = (bu) => setBuFilters(prev => {
    const n = new Set(prev);
    // If clicking the same sole active filter, clear it; otherwise set exclusively
    if (n.size === 1 && n.has(bu)) { n.clear(); } else { n.clear(); n.add(bu); }
    return n;
  });
  const toggleDir = (dir) => setDirFilters(prev => { const n = new Set(prev); n.has(dir) ? n.delete(dir) : n.add(dir); return n; });

  const handleUpdate = useCallback(async () => {
    if (!selectedTeam) { toast.error('Please select a team'); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await api.get(`${API}/zoho/market-sector/calls`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { team_id: selectedTeam, start_date: startDate, end_date: endDate },
      });
      const newCalls = r.data?.calls || [];
      setCalls(newCalls);
      setFetched(true);

      let newQuotesData = null;
      const accountIds = [...new Set(newCalls.map(c => c.account_id).filter(Boolean))];
      if (accountIds.length) {
        try {
          const qr = await api.get(`${API}/zoho/market-sector/quotes-summary`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { account_ids: accountIds.join(','), start_date: startDate, end_date: endDate },
          });
          newQuotesData = qr.data?.by_account || null;
        } catch { /* non-fatal */ }
      }
      setQuotesData(newQuotesData);
      saveCachedState({ selectedTeam, startDate, endDate, calls: newCalls, quotesData: newQuotesData });

      const count = r.data?.total || 0;
      toast.success(`Loaded ${count} call${count !== 1 ? 's' : ''} for ${r.data?.team_name || 'team'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load call data');
    } finally {
      setLoading(false);
    }
  }, [selectedTeam, startDate, endDate]);

  // Apply filters
  const filtered = calls.filter(c => {
    if (buFilters.size && !buFilters.has(c.business_unit || 'Unknown')) return false;
    if (dirFilters.size && !dirFilters.has(c.direction || 'outgoing')) return false;
    return true;
  });

  // Group by owner
  const byOwner = filtered.reduce((acc, c) => {
    const k = c.owner_name || 'Unknown';
    if (!acc[k]) acc[k] = [];
    acc[k].push(c);
    return acc;
  }, {});
  const owners = Object.keys(byOwner).sort();

  const buCounts = BUS.reduce((acc, bu) => { acc[bu] = filtered.filter(c => c.business_unit === bu).length; return acc; }, {});
  const outgoing = filtered.filter(c => c.direction !== 'incoming').length;
  const incoming = filtered.filter(c => c.direction === 'incoming').length;

  // ── KPI enhancements: Phocas sales + unique quotes per BU ─────────────────
  const buStats = useMemo(() => {
    // All Phocas mode: use sector totals from Phocas file (all accounts, not just called)
    if (!callsOnly && phocasData?.sectorTotals && Object.keys(phocasData.sectorTotals).length > 0) {
      const result = {};
      BUS.forEach(bu => {
        const st = phocasData.sectorTotals[bu] || {};
        const buCalls = calls.filter(c => c.business_unit === bu);
        const seenIds = new Set();
        let quotesCount = 0, quotesValue = 0, ordersCount = 0, ordersValue = 0;
        buCalls.forEach(c => {
          if (!c.account_id || seenIds.has(c.account_id)) return;
          seenIds.add(c.account_id);
          const qd = quotesData?.[c.account_id];
          if (qd) {
            quotesCount += qd.count || 0;
            quotesValue += qd.total_value || 0;
            (qd.quotes || []).forEach(q => {
              if ((q.stage || '').includes('Order') || (q.stage || '').includes('Won')) {
                ordersCount++;
                ordersValue += q.total || 0;
              }
            });
          }
        });
        result[bu] = {
          phocasTotal: st.value || 0,
          uniqueAccounts: st.count || 0,
          quotesCount, quotesValue, ordersCount, ordersValue,
        };
      });
      return result;
    }
    // Calls-only mode (original): sum Phocas only for called accounts
    const norm = phocasData?.normLookup;
    const result = {};
    BUS.forEach(bu => {
      const buCalls = calls.filter(c => c.business_unit === bu);
      const seenIds = new Set();
      let phocasTotal = 0, quotesCount = 0, quotesValue = 0, ordersCount = 0, ordersValue = 0;
      buCalls.forEach(c => {
        if (!c.account_id || seenIds.has(c.account_id)) return;
        seenIds.add(c.account_id);
        const match = norm ? matchPhocas(c.account_name, norm) : null;
        if (match) phocasTotal += (match.value || 0);
        const qd = quotesData?.[c.account_id];
        if (qd) {
          quotesCount += qd.count || 0;
          quotesValue += qd.total_value || 0;
          (qd.quotes || []).forEach(q => {
            if ((q.stage || '').includes('Order') || (q.stage || '').includes('Won')) {
              ordersCount++;
              ordersValue += q.total || 0;
            }
          });
        }
      });
      result[bu] = { phocasTotal, quotesCount, quotesValue, ordersCount, ordersValue, uniqueAccounts: seenIds.size };
    });
    return result;
  }, [calls, phocasData, quotesData, callsOnly]);

  const totalStats = useMemo(() => {
    // All Phocas mode: sum totals across all sectors
    if (!callsOnly && phocasData?.sectorTotals && Object.keys(phocasData.sectorTotals).length > 0) {
      let phocasTotal = 0;
      Object.values(phocasData.sectorTotals).forEach(st => { phocasTotal += st.value || 0; });
      const seenIds = new Set();
      let quotesCount = 0, quotesValue = 0, ordersCount = 0, ordersValue = 0;
      calls.forEach(c => {
        if (!c.account_id || seenIds.has(c.account_id)) return;
        seenIds.add(c.account_id);
        const qd = quotesData?.[c.account_id];
        if (qd) {
          quotesCount += qd.count || 0;
          quotesValue += qd.total_value || 0;
          (qd.quotes || []).forEach(q => {
            if ((q.stage || '').includes('Order') || (q.stage || '').includes('Won')) {
              ordersCount++;
              ordersValue += q.total || 0;
            }
          });
        }
      });
      return { phocasTotal, quotesCount, quotesValue, ordersCount, ordersValue };
    }
    // Calls-only mode (original)
    const norm = phocasData?.normLookup;
    const seenIds = new Set();
    let phocasTotal = 0, quotesCount = 0, quotesValue = 0, ordersCount = 0, ordersValue = 0;
    calls.forEach(c => {
      if (!c.account_id || seenIds.has(c.account_id)) return;
      seenIds.add(c.account_id);
      const match = norm ? matchPhocas(c.account_name, norm) : null;
      if (match) phocasTotal += (match.value || 0);
      const qd = quotesData?.[c.account_id];
      if (qd) {
        quotesCount += qd.count || 0;
        quotesValue += qd.total_value || 0;
        (qd.quotes || []).forEach(q => {
          if ((q.stage || '').includes('Order') || (q.stage || '').includes('Won')) {
            ordersCount++;
            ordersValue += q.total || 0;
          }
        });
      }
    });
    return { phocasTotal, quotesCount, quotesValue, ordersCount, ordersValue };
  }, [calls, phocasData, quotesData, callsOnly]);

  // Build bu_totals payload for report generation
  const buTotals = useMemo(() => {
    const result = {};
    BUS.forEach(bu => {
      result[bu] = {
        call_count: buCounts[bu] || 0,
        unique_accounts: (!callsOnly && phocasData?.sectorTotals?.[bu]?.count)
          ? phocasData.sectorTotals[bu].count
          : (buStats[bu]?.uniqueAccounts || 0),
        quotes_count: buStats[bu]?.quotesCount || 0,
        quotes_value: Math.round(buStats[bu]?.quotesValue || 0),
        orders_count: buStats[bu]?.ordersCount || 0,
        orders_value: Math.round(buStats[bu]?.ordersValue || 0),
        phocas_sales: Math.round(buStats[bu]?.phocasTotal || 0),
      };
    });
    return result;
  }, [buCounts, buStats, callsOnly, phocasData]);

  // ── CRM Sales Report modal state ────────────────────────────────────────────
  const [showCrmReport, setShowCrmReport] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null); // {id, name} to open viewer

  const handleReportCreated = useCallback((report) => {
    setSelectedReport({ id: report.id, name: report.name });
  }, []);

  // Teams lookup for report naming
  const teamName = useMemo(() => {
    const t = teams.find(t => t.id === selectedTeam);
    return t?.team_name || '';
  }, [teams, selectedTeam]);

  // Update a single call's notes in local state (called after successful CRM save)
  const handleNoteUpdated = useCallback((callId, newNote) => {
    setCalls(prev => prev.map(c => c.id === callId ? { ...c, notes: newNote } : c));
  }, []);

  // Update a single call's business unit in local state
  const handleBuUpdated = useCallback((callId, newBu) => {
    setCalls(prev => prev.map(c => c.id === callId ? { ...c, business_unit: newBu } : c));
  }, []);

  // Open quotes detail modal for a specific call
  const [quotesModal, setQuotesModal] = useState(null); // { accountName, quotes }
  const handleOpenQuotes = useCallback((call) => {
    const info = quotesData ? (quotesData[call.account_id] ?? null) : null;
    setQuotesModal({ accountName: call.account_name, quotes: info?.quotes || [] });
  }, [quotesData]);

  return (
    <div className="space-y-4" data-testid="market-sector-view">
      {/* Controls bar */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Sales Team</label>
            <Select value={selectedTeam} onValueChange={setSelectedTeam} data-testid="market-team-select">
              <SelectTrigger className="rounded-xl h-10 text-sm border-gray-200">
                <SelectValue placeholder="Select team…" />
              </SelectTrigger>
              <SelectContent>
                {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.team_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">From</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="rounded-xl border border-gray-200 h-10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
              data-testid="market-start-date" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">To</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="rounded-xl border border-gray-200 h-10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
              data-testid="market-end-date" />
          </div>
          <Button onClick={handleUpdate} disabled={loading || !selectedTeam}
            className="h-10 px-5 rounded-xl text-white font-semibold"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}
            data-testid="market-update-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Update
          </Button>
          {fetched && filtered.length > 0 && (
            <Button variant="outline" onClick={() => setShowExport(true)}
              className="h-10 px-4 rounded-xl border-sky-200 text-sky-700 hover:bg-sky-50"
              data-testid="market-export-btn">
              <Download className="w-4 h-4 mr-1.5" /> Export
            </Button>
          )}
          {fetched && calls.length > 0 && (
            <Button
              onClick={() => setShowCrmReport(true)}
              className="h-10 px-4 rounded-xl text-white gap-1.5"
              style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}
              data-testid="market-gen-report-btn"
            >
              <Sparkles className="w-4 h-4" /> Generate Sales Report
            </Button>
          )}
          {/* Phocas data mode toggle — only shown when sector headers detected in file */}
          {phocasData?.phocasSectors && Object.keys(phocasData.phocasSectors).length > 0 && (
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-xs text-gray-400 font-medium">Phocas:</span>
              <button
                onClick={() => setCallsOnly(false)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  !callsOnly ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-gray-500 border-gray-200 hover:border-sky-300 hover:text-sky-600'
                }`}
                data-testid="phocas-mode-all"
              >
                All Data
              </button>
              <button
                onClick={() => setCallsOnly(true)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  callsOnly ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-700'
                }`}
                data-testid="phocas-mode-calls"
              >
                Calls Only
              </button>
            </div>
          )}
        </div>

        {/* Phocas upload */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <PhocasUploadZone
            onParsed={(result, fileName) => {
              const withMeta = { ...result, fileName, savedAt: new Date().toISOString() };
              setPhocasData(withMeta);
              savePhocasCache(withMeta);
              // Persist globally so all users see the latest upload
              const token = localStorage.getItem('token');
              api.post(`${API}/zoho/phocas-data`, {
                byName: result.byName,
                prevByName: result.prevByName || {},
                phocasSectors: result.phocasSectors || {},
                rowCount: result.rowCount,
                periodLabel: result.periodLabel || '',
                prevPeriodLabel: result.prevPeriodLabel || '',
                fileName: fileName || '',
              }, { headers: { Authorization: `Bearer ${token}` } })
                .then(r => {
                  if (r.data?.uploaded_by) {
                    withMeta.uploaded_by = r.data.uploaded_by;
                    withMeta.savedAt = r.data.savedAt;
                    setPhocasData({ ...withMeta });
                  }
                })
                .catch(() => {}); // silently ignore — local state already updated
            }}
            currentData={phocasData}
            selectedPeriodLabel={phocasPeriodLabel}
          />
        </div>
      </div>

      {/* Filters */}
      {fetched && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400 mr-1">
              <Building2 className="w-3.5 h-3.5 inline mr-0.5" /> BU
            </span>
            <Chip label="All" active={buFilters.size === 0} onClick={() => setBuFilters(new Set())} testId="bu-filter-all" />
            {BUS.map(bu => (
              <Chip key={bu} label={`${bu} (${buCounts[bu]})`} active={buFilters.has(bu)}
                onClick={() => toggleBu(bu)} testId={`bu-filter-${bu.toLowerCase()}`} />
            ))}
          </div>
          <div className="w-px h-6 bg-gray-200 hidden sm:block" />
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400 mr-1">Direction</span>
            <Chip label="All" active={dirFilters.size === 0} onClick={() => setDirFilters(new Set())} testId="dir-filter-all" />
            <Chip label={`Outgoing (${outgoing})`} active={dirFilters.has('outgoing')} onClick={() => toggleDir('outgoing')} testId="dir-filter-outgoing" color="orange" />
            <Chip label={`Incoming (${incoming})`} active={dirFilters.has('incoming')} onClick={() => toggleDir('incoming')} testId="dir-filter-incoming" />
          </div>
        </div>
      )}

      {/* Stats row — BU cards are now clickable to filter */}
      {fetched && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {/* Total Calls */}
          <div className="rounded-xl p-3 text-center bg-gray-900 text-white" data-testid="stat-total-calls">
            <p className="text-2xl font-black">{filtered.length}</p>
            <p className="text-xs font-semibold opacity-80 mt-0.5">Total Calls</p>
            {phocasData?.byName && (
              <div className="mt-2 pt-2 border-t border-white/20 space-y-0.5">
                <p className="text-[11px] font-bold text-sky-300" data-testid="stat-total-phocas">
                  {totalStats.phocasTotal >= 1
                    ? `£${Math.round(totalStats.phocasTotal).toLocaleString('en-GB')}`
                    : '—'
                  }
                </p>
                <p className="text-[9px] uppercase tracking-wider opacity-60">Phocas Sales</p>
              </div>
            )}
            {quotesData && (
              <div className="mt-1.5 space-y-0.5">
                <p className="text-[11px] font-bold text-emerald-300" data-testid="stat-total-quotes">
                  {totalStats.quotesCount} quotes · £{Math.round(totalStats.quotesValue).toLocaleString('en-GB')}
                </p>
                <p className="text-[9px] uppercase tracking-wider opacity-60">Unique Quoted Accts</p>
              </div>
            )}
          </div>
          {BUS.map(bu => {
            const isActive = buFilters.has(bu);
            const bs = buStats[bu] || {};
            return (
              <button
                key={bu}
                type="button"
                onClick={() => toggleBu(bu)}
                className={`rounded-xl p-3 text-center transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98] ${
                  isActive
                    ? 'bg-sky-600 text-white shadow-md'
                    : 'bg-sky-50 text-sky-800 border border-sky-200 hover:bg-sky-100'
                }`}
                data-testid={`stat-${bu.toLowerCase()}`}
                title={`Click to filter by ${bu}`}
              >
                <p className="text-2xl font-black">{buCounts[bu]}</p>
                <p className="text-xs font-semibold opacity-80 mt-0.5">{bu}</p>
                {phocasData?.byName && (
                  <div className={`mt-2 pt-2 border-t ${isActive ? 'border-white/20' : 'border-sky-200'} space-y-0.5`}>
                    <p className={`text-[11px] font-bold ${isActive ? 'text-sky-200' : 'text-sky-600'}`} data-testid={`stat-${bu.toLowerCase()}-phocas`}>
                      {bs.phocasTotal >= 1
                        ? `£${Math.round(bs.phocasTotal).toLocaleString('en-GB')}`
                        : '—'
                      }
                    </p>
                    <p className="text-[9px] uppercase tracking-wider opacity-60">Phocas Sales</p>
                  </div>
                )}
                {quotesData && (
                  <div className="mt-1.5 space-y-0.5">
                    <p className={`text-[11px] font-bold ${isActive ? 'text-emerald-200' : 'text-emerald-600'}`} data-testid={`stat-${bu.toLowerCase()}-quotes`}>
                      {bs.quotesCount} q · £{Math.round(bs.quotesValue).toLocaleString('en-GB')}
                    </p>
                    <p className="text-[9px] uppercase tracking-wider opacity-60">Quotes</p>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Call list */}
      {loading && (
        <div className="py-20 text-center text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
          <p className="text-sm">Fetching calls from Zoho…</p>
        </div>
      )}
      {!loading && fetched && filtered.length === 0 && (
        <div className="py-16 text-center text-gray-400">
          <p className="text-sm">No calls match the current filters.</p>
        </div>
      )}
      {!loading && fetched && filtered.length > 0 && owners.map(owner => (
        <OwnerGroup
          key={owner}
          ownerName={owner}
          calls={byOwner[owner]}
          quotesData={quotesData}
          phocasNormLookup={phocasData?.normLookup || null}
          phocasPrevNormLookup={phocasData?.prevNormLookup || null}
          phocasPeriodLabel={phocasData?.periodLabel || null}
          phocasPrevPeriodLabel={phocasData?.prevPeriodLabel || null}
          onOpenCustomer={setCustomerModalName}
          onNoteUpdated={handleNoteUpdated}
          onBuUpdated={handleBuUpdated}
          onOpenQuotes={handleOpenQuotes}
        />
      ))}
      {!loading && !fetched && (
        <div className="py-16 text-center text-gray-400">
          <p className="text-sm">Select a team and date range, then click <strong>Update</strong> to load call data.</p>
        </div>
      )}

      {showExport && <ExportModal calls={filtered} onClose={() => setShowExport(false)} />}
      {quotesModal && (
        <QuotesDetailModal
          accountName={quotesModal.accountName}
          quotes={quotesModal.quotes}
          onClose={() => setQuotesModal(null)}
        />
      )}
      <ContactNotesTasksModal
        isOpen={!!customerModalName}
        onClose={() => setCustomerModalName(null)}
        initialSearch={customerModalName}
      />

      {/* CRM Sales Report creation modal */}
      <CrmSalesReportModal
        isOpen={showCrmReport}
        onClose={() => setShowCrmReport(false)}
        calls={calls}
        quotesData={quotesData}
        phocasData={phocasData}
        startDate={startDate}
        endDate={endDate}
        teamName={teamName}
        buTotals={buTotals}
        callsOnly={callsOnly}
        phocasSectors={phocasData?.phocasSectors || {}}
        onCreated={handleReportCreated}
      />

      {/* Report viewer */}
      {selectedReport && (
        <TeamReportViewerModal
          isOpen={!!selectedReport}
          reportSummary={selectedReport}
          onClose={() => setSelectedReport(null)}
        />
      )}
    </div>
  );
}
